import './init/polyfills';
import './init/fallback-local-storage';
import './init/sentry-init';
import './init/handle-dynamic-load-error';
import 'regenerator-runtime/runtime';
import * as amplitude from '@amplitude/analytics-browser';
import {sessionReplayPlugin} from '@amplitude/plugin-session-replay-browser';
import {onlineManager} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import i18n from 'i18next';
import HttpApi from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {HelmetProvider} from 'react-helmet-async';
import {initReactI18next} from 'react-i18next';
import {BrowserRouter} from 'react-router-dom';
import {ToastContainer} from 'react-toastify';
import {RecoilRoot} from 'recoil';
import InstillQueryClientProvider from 'ui/@contexts/instill-query-client-provider';
import {ThemeContextProvider} from 'ui/@contexts/theme-context';
import {QueryParamProvider} from 'use-query-params';
import {ReactRouter6Adapter} from 'use-query-params/adapters/react-router-6';
import isLocalHost from 'utils/environment-utils/is-local-host';
import appPackage from '../package.json';
import './index.scss';
import ApplicationRoutes from './ui/application-routes';
import Auth0ProviderWithRedirectCallback from './ui/auth0';
import env from 'constants/env';
import GoogleTagManager from 'ui/google-tag-manager';
import AppStartLoader from 'ui/@_components/app-start-loader';

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

onlineManager.setOnline(true);

i18n
  .use(HttpApi)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    defaultNS: 'common',
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'fr'],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    backend: {
      loadPath: (lng: string | string[], ns: string | string[]) => {
        const namespace = Array.isArray(ns) ? ns[0] : ns;
        const language = Array.isArray(lng) ? lng[0] : lng;
        if (!namespace || !language) return '';
        const kebabNs = namespace
          .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
          .toLowerCase();
        return `/locales/${language}/${kebabNs}.json?v=${__BUILD_ID__}`;
      },
    },
    returnNull: false,
    interpolation: {
      escapeValue: false,
    },
  });

if (Boolean(env.VITE_AMPLITUDE_API_KEY)) {
  const appVersion = env.VITE_AMPLITUDE_ENVIRONMENT_NAME
    ? `${env.VITE_AMPLITUDE_ENVIRONMENT_NAME}-${appPackage.version}`
    : appPackage.version;

  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: 1,
  });

  amplitude.add(sessionReplayTracking);

  amplitude.init(env.VITE_AMPLITUDE_API_KEY, undefined, {
    appVersion: appVersion,
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <React.Suspense fallback={<AppStartLoader />}>
      <RecoilRoot>
        <HelmetProvider>
          <GoogleTagManager />

          <BrowserRouter>
            <Auth0ProviderWithRedirectCallback>
              <InstillQueryClientProvider>
                <QueryParamProvider adapter={ReactRouter6Adapter}>
                  <ThemeContextProvider>
                    <ToastContainer
                      position="top-center"
                      autoClose={5000}
                      closeOnClick
                      hideProgressBar={false}
                      pauseOnFocusLoss={false}
                      theme="colored"
                      closeButton={true}
                    />
                    <ApplicationRoutes />
                  </ThemeContextProvider>
                </QueryParamProvider>

                {isLocalHost && (
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    position="bottom-right"
                  />
                )}
              </InstillQueryClientProvider>
            </Auth0ProviderWithRedirectCallback>
          </BrowserRouter>
        </HelmetProvider>
      </RecoilRoot>
    </React.Suspense>
  </React.StrictMode>
);
